import { RecordAny } from '@dealstack/shared/lib/common/common';
import { Badge, BadgeProps } from '@mantine/core';
import {
	DetailsDiv,
	InfoDiv,
	UniqueDiv,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import styled from 'styled-components';
import {
	ExtendedColumn,
	ExtendedColumnDef,
} from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';
import { TableFilterCheckboxSelect } from '@dealstack/frontend/shared/components/Table/MantineTable/TableFilterCheckboxSelect';

type Props<T extends RecordAny> = ExtendedColumnDef<T> & {
	headerText?: string;
	accessorKey?: keyof T;
	badgeMapping: Record<string, { backgroundColor: string; text: string; fontColor: string }>;
	showFooter?: boolean;
	showFilter?: boolean;
	footerValue?: string | number;
	text?: string;
	filterText?: string;
	badgeLimit?: number;
};

function isArrayOfRecords(value: unknown): value is Array<Record<string, string>> {
	return Array.isArray(value);
}

export const createOutlineBadgeColumn = <T extends RecordAny, K extends keyof T>({
	badgeMapping,
	showFooter = false,
	showFilter = false,
	footerValue,
	text,
	filterText,
	badgeLimit,
	headerText,
	enableSorting = true,
	...columnProps
}: Props<T>): ExtendedColumnDef<T> => {
	return {
		...columnProps,
		accessorKey: columnProps.accessorKey ?? columnProps.id ?? 'badge',
		header: headerText
			? createSortableTextHeader(headerText, enableSorting)
			: columnProps.header,
		columnLabel: columnProps.columnLabel ?? headerText ?? '',
		cell: ({ cell }) => {
			// this can be an array or a string
			const value = cell.getValue<T[K]>() as keyof typeof badgeMapping;

			if (isArrayOfRecords(value) && value.length) {
				const valuesToMap = value.slice(0, badgeLimit) as unknown as Array<
					Record<string, string>
				>;

				return (
					<>
						{valuesToMap.map((v) => {
							const badgeInfo = badgeMapping[v.name];

							if (!badgeInfo) {
								return (
									<BadgeWrapper bg="blue.1" c="blue.9" fw={400}>
										{v.name}
									</BadgeWrapper>
								);
							}

							return (
								<BadgeWrapper
									variant="outline"
									color={badgeInfo.backgroundColor}
									c={badgeInfo.fontColor}
									fw={400}
								>
									{badgeInfo.text}
								</BadgeWrapper>
							);
						})}
					</>
				);
			}

			const badgeInfo = badgeMapping[value];

			if (!badgeInfo) {
				return null;
			}

			return (
				<Badge
					color={badgeInfo.backgroundColor}
					c={badgeInfo.fontColor}
					fw={400}
					variant="outline"
				>
					{badgeInfo.text}
				</Badge>
			);
		},
		filter: showFilter
			? ({ column }) => (
					<TableFilterCheckboxSelect
						column={column as ExtendedColumn<unknown>}
						filterText={filterText}
						multiple
					/>
				)
			: undefined,
		footer: showFooter
			? () => (
					<DetailsDiv>
						<UniqueDiv>{text}</UniqueDiv>
						<InfoDiv>{footerValue}</InfoDiv>
					</DetailsDiv>
				)
			: undefined,
	};
};

const BadgeWrapper = styled(Badge)<BadgeProps>`
	margin-right: 0.5rem;
`;
